import * as THREE from 'three';
import { basic, emis, std, stone } from '../materials.ts';
import { G, M } from '../prims.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * El Ventisquero (docs/sendas/guerrero.md §5): a giant of packed snow and stone, three heroes tall, with no face —
 * two hollows where the wind gathers, a cold blue down in them. It forms every winter on the far face of the Spine and
 * climbs ahead of the Marea to the pass. Built of drifts heaped on drifts (the snow pale, its folds shaded blue), slabs
 * of the Spine's grey rock caught in it like bones, rime and icicles hanging off the shoulders and the arms, its fists
 * two boulders wrapped in snow. A nightmare: it comes apart into powder and comes back, so a thin veil of snow blows
 * off its back (a few pale ribbons, the boil frames).
 *
 * Its cubs: balls of snow as tall as a hero's knee that walk on stumps, two small hollows for eyes, a stone or two in
 * them. No rig: the boil frames (a little wobble) are all their motion; the fight moves them.
 *
 * Colors: snow #e9eef5 shading to #9fb2cb, the Spine's rock #5b6270, the hollows #1b2a44 with a cold glow; no gold.
 */

const SNOW = '#e9eef5';
const SNOW_D = '#9db0c9';
const SNOW_DD = '#7189aa';
const ROCK = '#454c59';

/** A drift lump: an icosahedron with its corners pushed in and out (corners shared move together). */
const lumpGeo = (rng: () => number, detail: number, amt: number): THREE.BufferGeometry => {
  const geo = new THREE.IcosahedronGeometry(1, detail);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const seen = new Map<string, number>();
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    const key = `${x.toFixed(3)},${y.toFixed(3)},${z.toFixed(3)}`;
    let d = seen.get(key);
    if (d === undefined) {
      d = 1 + (rng() - 0.5) * amt;
      seen.set(key, d);
    }
    pos.setXYZ(i, x * d, y * d, z * d);
  }
  geo.computeVertexNormals();
  return geo;
};

export const ventisquero = (o?: BaseOpts): Model => {
  const c = begin(o, 1.1, 905);
  const { g, rng } = c;
  const snow = std(SNOW, { roughness: 0.9, flatShading: true });
  const snowD = std(SNOW_D, { roughness: 0.95, flatShading: true });
  const snowDD = std(SNOW_DD, { roughness: 1, flatShading: true });
  const rock = stone(ROCK);
  const ice = std('#bcd6ec', { roughness: 0.2, metalness: 0.05, transparent: true, opacity: 0.85, flatShading: true });
  const hollow = std('#1b2a44', { roughness: 1 });
  const glow = emis('#3f6fae', '#2f5fa8', 0.8);
  const veil = basic('#eef4fb', 0.35);
  const lump = (parent: THREE.Object3D, mat: THREE.Material, x: number, y: number, z: number, sx: number, sy: number, sz: number, detail = 1): THREE.Mesh => {
    const m = M(lumpGeo(rng, detail, detail ? 0.22 : 0.3), mat, parent);
    m.position.set(x, y, z);
    m.scale.set(sx, sy, sz);
    m.rotation.set((rng() - 0.5) * 0.5, rng() * 6.3, (rng() - 0.5) * 0.5);
    return m;
  };
  // Legs: two heaps of packed snow, wider at the foot, rock showing at the knees.
  for (const k of [-1, 1]) {
    lump(g, snowD, k * 0.42, 0.32, 0.05, 0.42, 0.4, 0.46);
    lump(g, snow, k * 0.4, 0.78, 0.0, 0.34, 0.38, 0.36);
    lump(g, rock, k * 0.46, 0.66, 0.2, 0.16, 0.12, 0.1, 0);
  }
  // The body: drifts heaped on drifts, leaning forward into the wind; slabs of rock caught in it like bones.
  const body = G(g, 0, 0, 0);
  body.rotation.x = 0.16;
  lump(body, snowD, 0, 1.25, 0, 0.72, 0.5, 0.6);
  lump(body, snow, 0, 1.72, 0.06, 0.84, 0.56, 0.66);
  lump(body, snow, 0.05, 2.18, 0.0, 0.92, 0.5, 0.7);
  lump(body, snowDD, -0.3, 1.5, -0.42, 0.42, 0.42, 0.3);
  for (const [x, y, z, s] of [
    [0.38, 1.6, 0.48, 0.24],
    [-0.42, 2.0, 0.44, 0.2],
    [0.1, 1.2, 0.5, 0.18],
    [0.5, 2.25, -0.3, 0.22],
  ] as const)
    lump(body, rock, x, y, z, s * 1.6, s * 1.25, s * 0.9, 0);
  // Shoulders, hunched high, rime and icicles off them.
  for (const k of [-1, 1]) {
    lump(body, snow, k * 0.86, 2.38, 0, 0.42, 0.36, 0.42);
    lump(body, rock, k * 0.95, 2.55, -0.08, 0.2, 0.14, 0.2, 0);
    for (let i = 0; i < 4; i++) {
      const ic = M(new THREE.ConeGeometry(0.05 + rng() * 0.03, 0.3 + rng() * 0.25, 5), ice, body);
      ic.position.set(k * (0.7 + i * 0.12), 2.05 - rng() * 0.08, 0.16 - i * 0.1);
      ic.rotation.x = Math.PI;
    }
  }
  // The head: no face. A cowl of snow, and two hollows where the wind gathers, cold light deep inside them.
  const head = G(body, 0, 2.72, 0.12);
  lump(head, snow, 0, 0, 0, 0.42, 0.38, 0.4);
  lump(head, snowD, 0, 0.18, -0.12, 0.46, 0.26, 0.4);
  // Two hollows, not eyes: long dark slits slanting down and out, sunk deep, a faint cold light far in them; a jagged
  // brow of rock and ice over them, and spikes of rime standing off the crown in the wind.
  for (const k of [-1, 1]) {
    const h = M(new THREE.BoxGeometry(0.2, 0.07, 0.2), hollow, head);
    h.position.set(k * 0.15, 0.02, 0.3);
    h.rotation.set(0, 0, -k * 0.35);
    const e = M(new THREE.BoxGeometry(0.1, 0.02, 0.04), glow, head);
    e.position.set(k * 0.15, 0.02, 0.37);
    e.rotation.z = -k * 0.35;
    lump(head, rock, k * 0.17, 0.12, 0.3, 0.16, 0.06, 0.12, 0);
  }
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI - Math.PI / 2;
    const sp = M(new THREE.ConeGeometry(0.06, 0.45 + rng() * 0.35, 5), ice, head);
    sp.position.set(Math.sin(a) * 0.32, 0.28, -0.1 + Math.cos(a) * 0.1 - 0.15);
    sp.rotation.set(-0.7, 0, Math.sin(a) * 0.6);
  }
  // Arms: long, the elbows low, the fists two boulders packed in snow, reaching the knees.
  for (const k of [-1, 1]) {
    const arm = G(body, k * 0.95, 2.3, 0.05);
    arm.rotation.z = k * 0.22;
    arm.rotation.x = -0.25;
    lump(arm, snow, 0, -0.35, 0, 0.26, 0.42, 0.26);
    lump(arm, snowD, k * 0.08, -0.9, 0.1, 0.24, 0.38, 0.24);
    lump(arm, rock, k * 0.08, -1.32, 0.15, 0.3, 0.26, 0.3, 0);
    lump(arm, snow, k * 0.06, -1.24, 0.12, 0.32, 0.18, 0.32);
    for (let i = 0; i < 3; i++) {
      const ic = M(new THREE.ConeGeometry(0.04, 0.22 + rng() * 0.2, 5), ice, arm);
      ic.position.set(k * 0.12, -0.6 - i * 0.22, -0.18);
      ic.rotation.x = Math.PI + 0.3;
    }
  }
  // The veil of snow blowing off its back: thin pale ribbons, a little different each boil frame.
  const ribbons: THREE.Mesh[] = [];
  for (let i = 0; i < 4; i++) {
    const r = M(new THREE.PlaneGeometry(0.5 + i * 0.12, 0.9), veil, body);
    r.position.set(-0.4 + i * 0.28, 2.3 + (i % 2) * 0.2, -0.62 - i * 0.05);
    r.rotation.set(0.6, 0.3 * (i - 1.5), 0);
    ribbons.push(r);
  }
  const tick = (n: number): void => {
    ribbons.forEach((r, i) => {
      r.rotation.x = 0.55 + Math.sin(n * 1.3 + i) * 0.12;
      r.scale.y = 0.9 + ((n + i) % 3) * 0.08;
    });
    head.rotation.y = Math.sin(n * 0.7) * 0.05;
  };
  return done(c, 3.1, tick);
};

/** A cub: a ball of snow on stumps, two hollows for eyes, a stone stuck in it. */
export const snowCub = (o?: BaseOpts): Model => {
  const c = begin(o, 0.36, 906);
  const { g, rng } = c;
  const snow = std(SNOW, { roughness: 0.9, flatShading: true });
  const snowD = std(SNOW_D, { roughness: 1, flatShading: true });
  const hollow = std('#1b2a44', { roughness: 1 });
  const body = M(lumpGeo(rng, 1, 0.2), snow, g);
  body.position.y = 0.42;
  body.scale.set(0.36, 0.32, 0.34);
  const top = M(lumpGeo(rng, 0, 0.3), snowD, g);
  top.position.set(0.04, 0.68, -0.06);
  top.scale.set(0.22, 0.12, 0.2);
  for (const k of [-1, 1]) {
    const leg = M(lumpGeo(rng, 0, 0.3), snowD, g);
    leg.position.set(k * 0.15, 0.1, 0.02);
    leg.scale.set(0.12, 0.12, 0.13);
    const e = M(new THREE.SphereGeometry(0.045, 8, 6), hollow, g);
    e.position.set(k * 0.1, 0.5, 0.3);
    e.scale.z = 0.5;
  }
  const st = M(lumpGeo(rng, 0, 0.3), stone(ROCK), g);
  st.position.set(-0.22, 0.48, 0.12);
  st.scale.setScalar(0.08);
  const tick = (n: number): void => {
    body.scale.y = 0.32 + (n % 2) * 0.012;
  };
  return done(c, 0.75, tick);
};
